/**
 * ContestEnterPage — Cutest Pluck contest entry submission.
 *
 * Routed at `/contest/enter` (behind `ProtectedRoute` — unauthed
 * users get bounced to `/login?next=/contest/enter` via the shared
 * component). The flow:
 *   1. On mount: `GET /api/contest/entries/me` to check if the user
 *      already has an entry. Two shapes:
 *        - 200: render the "you're in the queue" status card.
 *        - 404: render the entry form.
 *   2. Form validation: dog_name required + ≤40 chars, caption
 *      ≤200 chars, photo picked (JPG/PNG only, ≤5 MB — mirrors the
 *      server-side constants), and the CONSENT checkbox checked.
 *      Submit disabled until every gate passes.
 *   3. Multipart POST — the endpoint is a `Form` + `File` shape,
 *      so we send FormData (not JSON).
 *   4. Success: swap to the status card in-place — same shape as
 *      the "already has an entry" branch — so a refresh + back-nav
 *      don't lose context.
 *
 * No hex colors, no `prefers-color-scheme` reads. All styling uses
 * the existing token classes (`bg-pp-surface`, `text-pp-text`,
 * `bg-pp-purple`, `text-brand-orange`) so the Family L guard is happy.
 */
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import PublicPageShell from "@/components/PublicPageShell";
import Logo from "@/components/Logo";
import { Loader2, CheckCircle2, ImagePlus, Trophy } from "lucide-react";
import { EntryShareButton } from "@/components/EntryShareButton";
import {
  CONTEST_CONSENT_TEXT,
  MAX_DOG_NAME_LEN,
  MAX_CAPTION_LEN,
  MAX_PHOTO_BYTES,
  ALLOWED_PHOTO_MIME,
} from "@/lib/contestConstants";

const STATUS_COPY = {
  pending: {
    title: "Your pup is in the queue!",
    body: "We'll review your entry shortly. Once approved you'll show up on the voting page.",
  },
  approved: {
    title: "You're in — good luck!",
    body: "Your entry has been approved and is live in the contest. Voting details coming soon.",
  },
  rejected: {
    title: "Entry not accepted",
    body: "See the reason below. You can start a fresh entry in the next round.",
  },
};

function StatusCard({ entry }) {
  const copy = STATUS_COPY[entry.status] || STATUS_COPY.pending;
  return (
    <div
      className="rounded-3xl border border-border bg-pp-surface p-8 shadow-sm"
      data-testid="contest-entry-status-card"
    >
      <div className="flex flex-col items-center text-center">
        <div className="rounded-full bg-pp-surface-2 p-4" aria-hidden="true">
          <CheckCircle2 className="h-8 w-8 text-brand-orange" />
        </div>
        <h2
          className="mt-4 font-heading text-2xl font-bold text-pp-text"
          data-testid="contest-entry-status-heading"
        >
          {copy.title}
        </h2>
        <p className="mt-3 text-sm text-muted-foreground">{copy.body}</p>
      </div>
      <div className="mt-6 space-y-2 rounded-2xl bg-pp-surface-2 p-4 text-sm text-pp-text">
        <div>
          <span className="font-semibold">Dog name: </span>
          <span data-testid="contest-entry-status-dog-name">{entry.dog_name}</span>
        </div>
        <div>
          <span className="font-semibold">Status: </span>
          <span
            className="uppercase tracking-wide text-brand-orange"
            data-testid="contest-entry-status-value"
          >
            {entry.status}
          </span>
        </div>
        {entry.status === "rejected" && entry.status_reason && (
          <div>
            <span className="font-semibold">Reason: </span>
            <span data-testid="contest-entry-status-reason">
              {entry.status_reason}
            </span>
          </div>
        )}
      </div>
      <div className="mt-6 flex flex-col gap-3">
        {entry.status === "approved" && (
          <div
            className="rounded-2xl border border-brand-orange/30 bg-brand-orange/5 p-5 text-center"
            data-testid="contest-entry-share-block"
          >
            <p className="mb-3 font-semibold text-pp-text">
              Now rally your friends and family to vote!
            </p>
            <div className="flex justify-center">
              <EntryShareButton entry={entry} variant="prominent" />
            </div>
          </div>
        )}
        <Link
          to="/"
          className="text-center text-sm font-semibold text-brand-orange hover:underline"
          data-testid="contest-entry-back-home"
        >
          Back home
        </Link>
      </div>
    </div>
  );
}

export default function ContestEnterPage() {
  // Lifecycle state machine:
  //   loading  → initial GET /me in flight
  //   form     → user has no entry, render the form
  //   status   → user has an entry (existing or just-submitted)
  //   error    → hard fetch failure on initial GET
  const [phase, setPhase] = useState("loading");
  const [entry, setEntry] = useState(null);

  // Form fields.
  const [dogName, setDogName] = useState("");
  const [caption, setCaption] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoError, setPhotoError] = useState("");
  const [consentChecked, setConsentChecked] = useState(false);
  // -----------------------------------------------------------------
  // LEGAL BASIS FOR MARKETING COMMUNICATIONS.
  // Explicit unchecked-by-default per Feb 2026 dispatch (Feature B).
  // This checkbox is the ONLY lawful basis for later marketing to
  // this entrant.
  //   - NEVER pre-check.
  //   - NEVER share state with the photo-consent checkbox above
  //     (`consentChecked` is a completely distinct piece of state
  //     with its own handler + its own submit-time FormData append).
  //   - NEVER auto-check from a saved value.
  // See `backend/contest_entries.py` POST /contest/entries handler
  // for the corresponding `audit_events` row + the one-way-commit
  // propagation to `users.marketing_opt_in`.
  // -----------------------------------------------------------------
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Feb 2026 dispatch — visible-feedback fix. `submitAttempted` flips
  // to true on the first submit click; inline error copy renders for
  // every required field the user has not yet satisfied. Prior UX
  // relied on `disabled={!canSubmit}` for the button — silent click
  // on invalid states → no user feedback → confirmed live-prod issue
  // (human observed empty-submit clicks with zero visible feedback).
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const fileInputRef = useRef(null);
  const validateFired = useRef(false);

  useEffect(() => {
    if (validateFired.current) return;
    validateFired.current = true;
    (async () => {
      try {
        const resp = await api.get("/contest/entries/me");
        setEntry(resp.data);
        setPhase("status");
      } catch (err) {
        if (err?.response?.status === 404) {
          setPhase("form");
        } else {
          setPhase("error");
        }
      }
    })();
  }, []);

  // Client-side gates. Names + booleans kept small so `canSubmit`
  // reads like a spec — a future reviewer can see the invariants
  // by scanning one line.
  const dogNameOk =
    dogName.trim().length > 0 && dogName.trim().length <= MAX_DOG_NAME_LEN;
  const captionOk = (caption || "").length <= MAX_CAPTION_LEN;
  const photoOk = photoFile != null && photoError === "";
  const canSubmit =
    dogNameOk && captionOk && photoOk && consentChecked && !submitting;

  // Feb 2026 — derived inline-error flags. Only render errors AFTER
  // the user has clicked submit at least once; showing errors before
  // any interaction would be shouting at the user for fields they
  // haven't reached yet. Once flipped, errors stay visible until the
  // corresponding field is satisfied.
  const showDogNameError = submitAttempted && !dogNameOk;
  const showPhotoRequiredError =
    submitAttempted && photoFile == null && photoError === "";
  const showConsentError = submitAttempted && !consentChecked;

  function onPhotoPicked(e) {
    const f = e.target.files && e.target.files[0];
    if (!f) {
      setPhotoFile(null);
      setPhotoError("");
      return;
    }
    if (!ALLOWED_PHOTO_MIME.includes(f.type)) {
      setPhotoFile(null);
      setPhotoError("Photo must be JPEG or PNG.");
      return;
    }
    if (f.size > MAX_PHOTO_BYTES) {
      setPhotoFile(null);
      setPhotoError("Photo must be 5 MB or smaller.");
      return;
    }
    setPhotoFile(f);
    setPhotoError("");
  }

  async function handleSubmit(e) {
    e.preventDefault();
    // Flip the submitAttempted gate FIRST so inline errors render
    // even when we early-return on !canSubmit. This is the fix for
    // the "no visible feedback on empty submit" issue observed in
    // production (Feb 2026 dispatch).
    setSubmitAttempted(true);
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("dog_name", dogName.trim());
      if (caption.trim()) fd.append("caption", caption.trim());
      fd.append("consent_given", "true");
      // Feature B — marketing opt-in is a SEPARATE FormData field.
      // Backend defaults to False if missing, so the "unchecked ->
      // omit" case would also be safe; sending the explicit value
      // makes the payload self-describing when replayed in a
      // compliance audit.
      fd.append("marketing_opt_in", marketingOptIn ? "true" : "false");
      fd.append("file", photoFile);
      const resp = await api.post("/contest/entries", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEntry(resp.data);
      setPhase("status");
      toast.success("Entry submitted! We'll review shortly.");
      // Commit B — §4.1 funnel step. Fires ONLY on genuine
      // POST-success (not on the 409 already-exists refresh
      // path below, which reflects a prior submission).
      trackEvent("contest.entry_submitted", { entry_id: resp.data?.id });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const errCode = typeof detail === "object" ? detail?.error : null;
      // If the user beat us to it via another tab, refresh the
      // page state to the server-authoritative view.
      if (err?.response?.status === 409 && errCode === "entry_already_exists") {
        toast.info("You already have an entry — refreshing your status.");
        const meResp = await api.get("/contest/entries/me");
        setEntry(meResp.data);
        setPhase("status");
        return;
      }
      toast.error(formatApiErrorDetail(detail) || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <PublicPageShell mainClass="flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-lg">
        <div className="mb-6 flex flex-col items-center text-center">
          <Link to="/" data-testid="contest-enter-logo-link" className="block">
            <Logo variant="full" className="h-24 w-24" />
          </Link>
          <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-pp-surface-2 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-brand-orange">
            <Trophy className="h-3 w-3" aria-hidden="true" />
            Cutest Pluck on the Block
          </div>
          <h1
            className="mt-3 font-heading text-3xl font-bold text-pp-text"
            data-testid="contest-enter-heading"
          >
            Enter your pup
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Upload a photo, name your dog, and add a fun caption.
          </p>
        </div>

        {phase === "loading" && (
          <div
            className="flex flex-col items-center justify-center rounded-3xl border border-border bg-pp-surface p-10 shadow-sm"
            data-testid="contest-enter-loading"
          >
            <Loader2 className="h-8 w-8 animate-spin text-brand-orange" aria-hidden="true" />
            <p className="mt-3 text-sm text-muted-foreground">Checking your entry status…</p>
          </div>
        )}

        {phase === "error" && (
          <div
            className="rounded-3xl border border-border bg-pp-surface p-8 text-center shadow-sm"
            data-testid="contest-enter-error"
          >
            <p className="text-base font-semibold text-pp-text">
              We couldn't load your entry status.
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              Please refresh the page or come back in a bit.
            </p>
          </div>
        )}

        {phase === "status" && entry && <StatusCard entry={entry} />}

        {phase === "form" && (
          <div className="rounded-3xl border border-border bg-pp-surface p-8 shadow-sm">
            <form
              onSubmit={handleSubmit}
              className="space-y-5"
              data-testid="contest-enter-form"
              encType="multipart/form-data"
            >
              <div>
                <Label htmlFor="ce-dog-name" className="text-sm font-semibold text-pp-text">
                  Dog name
                </Label>
                <Input
                  id="ce-dog-name"
                  type="text"
                  value={dogName}
                  onChange={(e) => setDogName(e.target.value)}
                  maxLength={MAX_DOG_NAME_LEN}
                  placeholder="Biscuit"
                  className="mt-2 h-12 rounded-full border-border bg-pp-surface px-5"
                  autoFocus
                  required
                  aria-invalid={showDogNameError ? "true" : "false"}
                  aria-describedby={showDogNameError ? "ce-dog-name-error" : undefined}
                  data-testid="contest-enter-dog-name"
                />
                {showDogNameError && (
                  <p
                    id="ce-dog-name-error"
                    className="mt-1 text-xs text-brand-orange"
                    data-testid="contest-enter-dog-name-error"
                    role="alert"
                  >
                    Dog name is required.
                  </p>
                )}
                <p className="mt-1 text-xs text-muted-foreground">
                  {dogName.trim().length}/{MAX_DOG_NAME_LEN}
                </p>
              </div>

              <div>
                <Label htmlFor="ce-caption" className="text-sm font-semibold text-pp-text">
                  Caption (optional)
                </Label>
                <Textarea
                  id="ce-caption"
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  maxLength={MAX_CAPTION_LEN}
                  placeholder="What makes your dog the cutest?"
                  className="mt-2 min-h-[96px] rounded-2xl border-border bg-pp-surface px-5 py-3"
                  data-testid="contest-enter-caption"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  {caption.length}/{MAX_CAPTION_LEN}
                </p>
              </div>

              <div>
                <Label className="text-sm font-semibold text-pp-text">Photo</Label>
                <div className="mt-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png"
                    onChange={onPhotoPicked}
                    className="hidden"
                    data-testid="contest-enter-photo-input"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    className="flex h-24 w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-border bg-pp-surface-2 text-sm text-muted-foreground hover:border-brand-orange hover:text-brand-orange"
                    data-testid="contest-enter-photo-picker"
                  >
                    <ImagePlus className="h-5 w-5" />
                    {photoFile ? photoFile.name : "Choose a JPG or PNG (≤5 MB)"}
                  </button>
                  {photoError && (
                    <p
                      className="mt-1 text-xs text-brand-orange"
                      data-testid="contest-enter-photo-error"
                      role="alert"
                    >
                      {photoError}
                    </p>
                  )}
                  {showPhotoRequiredError && (
                    <p
                      className="mt-1 text-xs text-brand-orange"
                      data-testid="contest-enter-photo-required-error"
                      role="alert"
                    >
                      Please choose a photo of your pup.
                    </p>
                  )}
                </div>
              </div>

              <div className="rounded-2xl bg-pp-surface-2 p-4">
                {/* Rules link placed BEFORE the consent checkbox
                    (Item A, 2026-07-23) so entrants can review the
                    rules before submitting. Deliberately quieter
                    typography than the consent copy — the checkbox
                    is the required interaction; the rules link is
                    a supporting affordance. */}
                <p className="mb-3 text-xs text-pp-text-muted">
                  Before you submit, please review the{" "}
                  <Link
                    to="/cutest-pluck/rules"
                    className="font-semibold text-pp-text underline decoration-pp-orange decoration-2 underline-offset-2 hover:text-pp-orange"
                    data-testid="contest-enter-rules-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Contest rules
                  </Link>
                  {" "}(opens in a new tab).
                </p>
                <label
                  className="flex items-start gap-3 text-sm text-pp-text"
                  htmlFor="ce-consent-checkbox"
                >
                  <Checkbox
                    id="ce-consent-checkbox"
                    checked={consentChecked}
                    onCheckedChange={(v) => setConsentChecked(v === true)}
                    aria-invalid={showConsentError ? "true" : "false"}
                    aria-describedby={showConsentError ? "ce-consent-error" : undefined}
                    data-testid="contest-enter-consent-checkbox"
                  />
                  <span data-testid="contest-enter-consent-text">
                    {CONTEST_CONSENT_TEXT}
                  </span>
                </label>
                {showConsentError && (
                  <p
                    id="ce-consent-error"
                    className="mt-2 text-xs text-brand-orange"
                    data-testid="contest-enter-consent-error"
                    role="alert"
                  >
                    Please tick the box to confirm you've read the rules.
                  </p>
                )}
              </div>

              {/* -----------------------------------------------------
                  LEGAL BASIS FOR MARKETING COMMUNICATIONS.
                  Explicit unchecked-by-default per Feb 2026 dispatch
                  (Feature B). This checkbox is the ONLY lawful basis
                  for later marketing to this entrant.
                    - NEVER pre-check.
                    - NEVER share state with the photo-consent checkbox
                      above (`consentChecked`) — separate state,
                      separate handler, separate FormData field.
                    - NEVER auto-check from a saved value.
                  Distinct wrapping <div> so the two consent gates are
                  visually AND structurally separate; a future contrib-
                  utor who "helpfully" collapses them into one row
                  should notice this comment first.
                  ---------------------------------------------------- */}
              <div className="rounded-2xl bg-pp-surface-2 p-4">
                <label
                  className="flex items-start gap-3 text-sm text-pp-text"
                  htmlFor="ce-marketing-optin-checkbox"
                >
                  <Checkbox
                    id="ce-marketing-optin-checkbox"
                    checked={marketingOptIn}
                    onCheckedChange={(v) => setMarketingOptIn(v === true)}
                    data-testid="contest-enter-marketing-optin-checkbox"
                  />
                  <span data-testid="contest-enter-marketing-optin-text">
                    Email me PooPluckers news and offers
                  </span>
                </label>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="h-12 w-full rounded-full bg-pp-purple text-base text-white hover:bg-pp-purple-dark"
                data-testid="contest-enter-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting…
                  </>
                ) : (
                  "Enter my pup"
                )}
              </Button>
            </form>
          </div>
        )}
      </div>
    </PublicPageShell>
  );
}
